import type { ComponentProps, ReactNode } from 'react';
import { UiLink } from '../labels';
import { cn } from '../lib/cn';
import { Icon, type IconName } from './icon';

/**
 * Vertical list of actions or links inside panels and sheets: profile menu, "More" on the phone,
 * settings navigation. Unlike `DropdownMenu` it is plain buttons and links, so it also works in a
 * bottom sheet.
 */
export function MenuList({ children, className, ...props }: ComponentProps<'ul'>) {
  return (
    <ul className={cn('flex flex-col p-1', className)} {...props}>
      {children}
    </ul>
  );
}

export interface MenuListItemProps {
  icon?: IconName;
  children: ReactNode;
  description?: ReactNode;
  /** Description up to two lines instead of one, e.g. a text excerpt of a search hit. */
  wrapDescription?: boolean;
  /** Right side: a value, badge or chevron. */
  trailing?: ReactNode;
  tone?: 'default' | 'danger';
  /** Marks the current entry (navigation). */
  active?: boolean;
  /** Renders a link of the app's router instead of a button. */
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

export function MenuListItem({
  icon,
  children,
  description,
  wrapDescription = false,
  trailing,
  tone = 'default',
  active = false,
  href,
  onClick,
  disabled,
  className,
}: MenuListItemProps) {
  const classes = cn(
    'flex min-h-control w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-left transition-colors transition-fast hover:bg-hover active:bg-pressed disabled:cursor-not-allowed disabled:text-fg-disabled',
    tone === 'danger' ? 'text-danger hover:bg-danger-subtle' : 'text-fg',
    active && 'bg-brand-subtle font-medium hover:bg-brand-subtle',
    className,
  );
  const body = (
    <>
      {icon && (
        <Icon
          name={icon}
          className={cn(
            'size-[1.125rem]',
            tone === 'danger' ? 'text-danger' : active ? 'text-brand' : 'text-fg-subtle',
          )}
        />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate">{children}</span>
        {description && (
          <span
            className={cn(
              'block text-xs font-normal text-fg-muted',
              wrapDescription ? 'line-clamp-2' : 'truncate',
            )}
          >
            {description}
          </span>
        )}
      </span>
      {trailing && <span className="shrink-0 text-fg-subtle">{trailing}</span>}
    </>
  );
  return (
    <li>
      {href ? (
        <UiLink
          href={href}
          aria-current={active ? 'page' : undefined}
          onClick={onClick}
          className={classes}
        >
          {body}
        </UiLink>
      ) : (
        <button type="button" onClick={onClick} disabled={disabled} className={classes}>
          {body}
        </button>
      )}
    </li>
  );
}

export function MenuListSeparator() {
  return <li role="separator" className="-mx-1 my-1 h-px bg-line" />;
}

export function MenuListLabel({ children }: { children: ReactNode }) {
  return (
    <li className="px-3 pt-3 pb-1 text-xs font-medium text-fg-subtle" role="presentation">
      {children}
    </li>
  );
}
